import { Flex, Spacer, Text, ThemeOverride, useTheme } from "@repo/ui";
import { useStyles } from "purse-styles";
import React from "react";

import CarnegieMellonUniversityLogo from "../assets/logos/cmu.svg?react";
import PalantirLogo from "../assets/logos/palantir.svg?react";
import YCombinatorLogo from "../assets/logos/y-combinator.svg?react";
import { TrackedEvent, trackEvent } from "../shared/analytics";
import { Card, TestimonialCard } from "./Cards";
import { HeroSubtitle, HeroTitle } from "./Typography";
// import { RequestDemoButton } from "./UI";

export { DemoSection } from "./DemoSection";

const StatCard: React.FC<{ value: string; description: string }> = ({
	value,
	description,
}) => {
	const gradientStyle = {
		background: `linear-gradient(135deg, #7840D2, #6253D9)`,
		WebkitBackgroundClip: "text",
		WebkitTextFillColor: "transparent",
		backgroundClip: "text",
	};

	return (
		<Flex
			column
			align="center"
			justify="center"
			paddingVertical="xl"
			paddingHorizontal="md"
			gap="xxs"
			mobile={{ paddingVertical: "md", paddingHorizontal: "sm" }}
			flex
			basis={240}
		>
			<Text
				size="xxxl"
				weight="bold"
				align="center"
				style={{ fontSize: 80, lineHeight: 1, ...gradientStyle }}
				mobile={{ style: { fontSize: 56 } }}
			>
				{value}
			</Text>
			<Spacer style={{ height: 8 }} mobile={{ style: { height: 4 } }} />
			<Text
				size="lg"
				mobile={{ size: "md" }}
				weight="medium"
				color="sand11"
				align="center"
				style={{ height: 52 }}
			>
				{description}
			</Text>
		</Flex>
	);
};

const HamiltonHealthboxLogo = new URL(
	"../assets/logos/hamilton-healthbox.png",
	import.meta.url,
);

const TobyHamiltonAvatar = new URL(
	"../assets/media/toby-hamilton.jpg",
	import.meta.url,
);

const EClinicalWorksLogo = new URL(
	"../assets/logos/eclinicalworks.png",
	import.meta.url,
);

const AthenaHealthLogo = new URL(
	"../assets/logos/athenahealth.png",
	import.meta.url,
);

const AzaleaLogo = new URL("../assets/logos/azalea.png", import.meta.url);

const CanvasLogo = new URL("../assets/logos/canvas.png", import.meta.url);

const ElationLogo = new URL("../assets/logos/elation.png", import.meta.url);

// Demo section removed per user request

export const HeaderSection: React.FC = () => {
	return (
		<>
			<Flex column align="stretch" style={{ position: "relative" }}>
				<Spacer
					style={{ height: "8vh" }}
					mobile={{ style: { height: "5vh" } }}
				/>
				<Flex column align="center" paddingHorizontal="lg">
					<HeroTitle>
						Automate specialist referrals from start to finish
					</HeroTitle>
				</Flex>
				<Spacer height="lg" />
				<Flex column align="center" paddingHorizontal="md">
					<HeroSubtitle>
						Let AI agents submit authorizations, book appointments, fax
						documents, notify patients and retrieve consult notes — all on
						autopilot
					</HeroSubtitle>
				</Flex>
				<Spacer style={{ height: 20 }} />
				{/* <Flex column align="center">
					<RequestDemoButton />
				</Flex> */}
				<Spacer style={{ height: 64 }} mobile={{ style: { height: 32 } }} />

				<Text
					align="center"
					font="Newsreader"
					italic
					size="xl"
					mobile={{ size: "lg" }}
					color="sand11"
					style={{ opacity: 0.8 }}
				>
					Founded by alumni of
				</Text>
				<Spacer height="xl" mobile={{ height: "lg" }} />

				<Flex
					align="center"
					alignSelf="center"
					justify="center"
					gap="xxxxl"
					tablet={{ gap: "xxl" }}
					mobile={{ gap: "xl", style: { marginBottom: "24px" } }}
					style={{ width: "100%", marginBottom: "48px" }}
				>
					<Flex
						style={{ maxWidth: 120, maxHeight: 50 }}
						tablet={{ style: { maxWidth: 100, maxHeight: 42 } }}
						mobile={{ style: { maxWidth: 80, maxHeight: 36 } }}
					>
						<PalantirLogo
							opacity={0.4}
							style={{ width: "100%", height: "100%" }}
						/>
					</Flex>
					<Flex
						style={{ maxWidth: 120, maxHeight: 50 }}
						tablet={{ style: { maxWidth: 100, maxHeight: 42 } }}
						mobile={{ style: { maxWidth: 80, maxHeight: 36 } }}
					>
						<CarnegieMellonUniversityLogo
							opacity={0.4}
							style={{ width: "100%", height: "100%" }}
						/>
					</Flex>
					<Flex
						style={{ maxWidth: 120, maxHeight: 50 }}
						tablet={{ style: { maxWidth: 100, maxHeight: 42 } }}
						mobile={{ style: { maxWidth: 80, maxHeight: 36 } }}
					>
						<YCombinatorLogo
							opacity={0.4}
							style={{ width: "100%", height: "100%" }}
						/>
					</Flex>
				</Flex>
			</Flex>
		</>
	);
};

export const TestimonialsSection: React.FC = () => {
	return (
		<Flex
			column
			align="center"
			paddingVertical="xxl"
			mobile={{ paddingVertical: "xl" }}
			paddingHorizontal="md"
		>
			<Spacer style={{ height: 96 }} mobile={{ style: { height: 32 } }} />
			<Text
				size="xxxl"
				mobile={{ size: "xxl" }}
				weight="semibold"
				align="center"
			>
				Proven Impact
			</Text>
			<Spacer style={{ height: 16 }} mobile={{ style: { height: 12 } }} />
			<Text
				size="lg"
				mobile={{ size: "md" }}
				color="sand11"
				align="center"
				balance
				style={{ maxWidth: 600, fontWeight: 450, lineHeight: 1.6 }}
			>
				We worked closely with Hamilton{"\u00A0"}Health{"\u00A0"}Box to
				streamline their referral process
			</Text>
			<Spacer style={{ height: 64 }} mobile={{ style: { height: 32 } }} />

			<Flex
				gap="xl"
				mobile={{ gap: "xs" }}
				wrap
				justify="center"
				align="stretch"
				style={{ maxWidth: 640, width: "100%" }}
			>
				<StatCard value="82%" description="Speed up in referral processing" />
				<StatCard value="55%" description="Sooner appointments found" />
				<StatCard value="44%" description="Fewer referral kickbacks" />
			</Flex>

			<Spacer style={{ height: 64 }} mobile={{ style: { height: 32 } }} />
			<TestimonialCard
				logo={HamiltonHealthboxLogo.toString()}
				logoAlt="Hamilton Health Box"
				quote={`At Hamilton Health Box, our model depends on absolute labor efficiency, with everyone operating "top of license". What used to be a major administrative burden now happens automatically and accurately in the background. We’ve seen faster processing times, fewer kickbacks, and happier staff. It’s rare to find a technology like Saffron that truly simplifies operations while saving both time and money.`}
				avatar={TobyHamiltonAvatar.toString()}
				avatarAlt="Dr. Toby Hamilton"
				name="Dr. Toby Hamilton"
				title="CEO, Hamilton Health Box"
				linkedInUrl="https://www.linkedin.com/in/dr-toby-hamilton-a6381010b/"
			/>
		</Flex>
	);
};

const LinkButton: React.FC<{
	children: React.ReactNode;
	href: string;
	trackingEvent: TrackedEvent;
}> = ({ children, href, trackingEvent }) => {
	const theme = useTheme();
	const className = useStyles({
		position: "relative",
		width: "fit-content",
		color: theme.colors.sand11,
		transition: "color 0.15s ease-in-out",
		"& > *": {
			position: "relative",
			zIndex: 1,
		},
		"&::after": {
			content: '""',
			position: "absolute",
			inset: "-3px -4px -3px -6px",
			borderRadius: 6,
			background: "transparent",
			transition: "background 0.15s ease-in-out",
			zIndex: 0,
		},
		"&:hover::after": {
			background: theme.colors.sandA4,
		},
		"&:hover *": {
			color: theme.colors.sand12,
		},
	});

	return (
		<a
			href={href}
			target="_blank"
			rel="noopener noreferrer"
			className={className}
			onClick={() => trackEvent(trackingEvent)}
		>
			<Flex align="center" gap="xxxs">
				{children}
				<ArrowUpRightIcon />
			</Flex>
		</a>
	);
};

const ArrowUpRightIcon: React.FC<{ size?: number }> = ({ size = 18 }) => (
	<svg
		width={size}
		height={size}
		fill="none"
		viewBox="0 0 24 24"
		aria-hidden="true"
	>
		<path
			stroke="currentColor"
			strokeLinecap="round"
			strokeLinejoin="round"
			strokeWidth="1.5"
			d="M17.25 15.25V6.75H8.75"
		></path>
		<path
			stroke="currentColor"
			strokeLinecap="round"
			strokeLinejoin="round"
			strokeWidth="1.5"
			d="M17 7L6.75 17.25"
		></path>
	</svg>
);

const SecurityCard: React.FC = () => {
	return (
		<Flex
			gap="xl"
			background="sandA3"
			borderWidth="md"
			borderColor="sandA6"
			align="center"
			style={{
				maxWidth: 800,
				position: "relative",
				zIndex: 1,
				borderRadius: 16,
				paddingLeft: 48,
				paddingRight: 48,
				paddingTop: 48,
				paddingBottom: 48,
				backdropFilter: "blur(20px)",
			}}
			mobile={{
				column: true,
				width: undefined,
				style: {
					borderRadius: 0,
					paddingLeft: 24,
					paddingRight: 24,
					borderRightWidth: 0,
					borderLeftWidth: 0,
					paddingTop: 32,
					paddingBottom: 32,
					marginLeft: -16,
					marginRight: -16,
				},
			}}
		>
			<Flex column flex style={{ minWidth: 0 }}>
				<Text size="xl" weight="semibold" style={{ marginBottom: 12 }}>
					Enterprise-grade security
				</Text>
				<Text
					color="sand11"
					style={{ fontWeight: 450, lineHeight: 1.6, marginBottom: 16 }}
				>
					We're HIPAA-compliant and have been audited by a third-party security
					vendor to ensure the highest standards of data protection.
				</Text>
				<LinkButton
					href="https://compliance.saffron.health/overview"
					trackingEvent={TrackedEvent.CLICK_TRUST_CENTER}
				>
					<Text size="sm" weight="medium" light>
						See our trust center
					</Text>
				</LinkButton>
			</Flex>
			<Flex
				shrink={0}
				mobile={{
					style: { alignSelf: "flex-end", marginTop: -52, marginRight: 20 },
				}}
			>
				<img
					src="/hipaa.png"
					alt="HIPAA Compliance Badge"
					width="260"
					height="260"
					style={{
						width: 130,
						height: "auto",
						transform: "rotate(6.7deg)",
						filter: "drop-shadow(0 4px 12px rgba(139, 158, 0, 0.4))",
					}}
				/>
			</Flex>
		</Flex>
	);
};

export const SecurityIntegrationsSection: React.FC = () => {
	const theme = useTheme();
	const marqueeClassName = useStyles({
		animation: "marquee 40s linear infinite",
		display: "flex",
		gap: "80px",
		alignItems: "center",
		paddingLeft: "40px",
		paddingRight: "40px",
		[`@media (max-width: ${theme.breakpoints.mobile}px)`]: {
			gap: "40px",
			paddingLeft: "20px",
			paddingRight: "20px",
		},
	});

	const ehrLogos = [
		{ src: EClinicalWorksLogo.toString(), alt: "eClinicalWorks", height: 20 },
		{ src: AthenaHealthLogo.toString(), alt: "Athena Health", height: 48 },
		{ src: AzaleaLogo.toString(), alt: "Azalea Health", height: 29 },
		{ src: CanvasLogo.toString(), alt: "Canvas Medical", height: 20 },
		// Elation Health logo has extra whitespace at the top, so we shift it up to align with other logos
		{
			src: ElationLogo.toString(),
			alt: "Elation Health",
			height: 26,
			transform: "translateY(-4px)",
		},
	];

	return (
		<Flex
			column
			align="center"
			paddingVertical="xxl"
			paddingHorizontal="md"
			mobile={{ paddingHorizontal: undefined }}
		>
			<Spacer style={{ height: 64 }} mobile={{ style: { height: 0 } }} />
			<Text
				size="xxxl"
				mobile={{ size: "xxl" }}
				weight="semibold"
				align="center"
			>
				Security & Integrations
			</Text>
			<Spacer style={{ height: 16 }} mobile={{ style: { height: 12 } }} />
			<Text
				size="lg"
				mobile={{ size: "md" }}
				color="sand11"
				align="center"
				style={{ maxWidth: 600, fontWeight: 450, lineHeight: 1.6 }}
			>
				Using a unique combination of API endpoints and AI browser agents lets
				us build integrations with any EHR system in weeks, not months.
			</Text>
			<Spacer style={{ height: 64 }} mobile={{ height: "lg" }} />

			{/* Marquee Section for EHR Logos */}
			<Flex
				column
				align="center"
				style={{ width: "100%", maxWidth: "100vw", position: "relative" }}
			>
				<Flex
					style={{
						width: "min(800px, 100vw)",
						height: 60,
						position: "relative",
						overflow: "hidden",
					}}
				>
					{/* Left divider */}
					<Flex
						background="sand6"
						style={{
							width: 2,
							height: 60,
							position: "absolute",
							left: 16,
							zIndex: 1,
							transform: "skewX(-12deg)",
						}}
						mobile={{ style: { display: "none" } }}
					/>

					{/* Right divider */}
					<Flex
						background="sand6"
						style={{
							width: 2,
							height: 60,
							position: "absolute",
							right: 16,
							zIndex: 1,
							transform: "skewX(-12deg)",
						}}
						mobile={{ style: { display: "none" } }}
					/>

					{/* Marquee container */}
					<Flex
						style={{
							position: "absolute",
							inset: 0,
							overflow: "hidden",
							clipPath:
								"polygon(25.8px 0, calc(100% - 12px) 0, calc(100% - 25.8px) 100%, 12px 100%)",
							maskImage:
								"linear-gradient(to right, transparent, black 120px, black calc(100% - 120px), transparent)",
						}}
						mobile={{ style: { clipPath: "none" } }}
					>
						<div className={marqueeClassName}>
							{ehrLogos.map((logo, idx) => (
								<Flex
									key={idx}
									align="center"
									justify="center"
									style={{ minWidth: 120 }}
								>
									<img
										src={logo.src}
										alt={logo.alt}
										style={{
											height: logo.height,
											width: "auto",
											objectFit: "contain",
											transform: logo.transform,
										}}
									/>
								</Flex>
							))}
						</div>
						<div className={marqueeClassName} aria-hidden="true">
							{ehrLogos.map((logo, idx) => (
								<Flex
									key={idx}
									align="center"
									justify="center"
									style={{ minWidth: 120 }}
								>
									<img
										src={logo.src}
										alt={logo.alt}
										style={{
											height: logo.height,
											width: "auto",
											objectFit: "contain",
											transform: logo.transform,
										}}
									/>
								</Flex>
							))}
						</div>
					</Flex>
				</Flex>

				<Spacer style={{ height: 16 }} />
				<LinkButton
					href="mailto:founders@saffron.health?subject=Question%20about%20EHR%20integration&body=Hi%2C%0A%0AI'd%20like%20to%20learn%20more%20about%20integrating%20Saffron%20with%20our%20EHR%20system."
					trackingEvent={TrackedEvent.CLICK_EHR_INTEGRATION_EMAIL}
				>
					<Text size="sm" weight="medium" color="sand11">
						Ask about yours
					</Text>
				</LinkButton>
			</Flex>

			<Spacer style={{ height: 96 }} />

			{/* Security Section */}
			<SecurityCard />
		</Flex>
	);
};

export const HowItWorksSection: React.FC = () => {
	return (
		<ThemeOverride mode="dark">
			<Flex
				column
				align="center"
				style={{
					position: "relative",
					zIndex: 2,
					overflowX: "clip",
					paddingBottom: 64,
				}}
				mobile={{ style: { paddingBottom: 48 } }}
			>
				<Flex
					className="grid-background-backward"
					borderTopWidth={"md"}
					borderBottomWidth={"md"}
					shadow={"strong"}
					borderColor="sand6"
					background="sand2"
					style={{
						position: "absolute",
						overflow: "hidden",
						inset: "-80px 0",
						transform: `skewY(2.7deg)`,
						zIndex: -1,
					}}
				/>
				<Flex column align="center" paddingVertical="md" paddingHorizontal="md">
					<Text
						size="xxxl"
						mobile={{ size: "xxl" }}
						weight="semibold"
						align="center"
						balance
						style={{ maxWidth: 520 }}
					>
						How Saffron works
					</Text>
					<Spacer style={{ height: 48 }} />

					<Flex gap="lg" wrap justify="center" align="stretch">
						<Card
							icon="FileUser"
							title="Step 1"
							description="Share your Medicare patient list with us and we'll integrate their health records into personalized care plans"
							background="sand4"
						/>
						<Card
							icon="PhoneCall"
							title="Step 2"
							description="Our team onboards patients to the Saffron mobile app and walks them through their care plans"
							background="sand4"
						/>
						<Card
							icon="AudioLines"
							title="Step 3"
							description="Patients receive 24/7 AI support for questions, symptom tracking, and care coordination"
							background="sand4"
						/>
						<Card
							icon="Receipt"
							title="Step 4"
							description="We handle APCM code billing, generating $180-$1,320 per patient annually"
							background="sand4"
						/>
					</Flex>
				</Flex>
			</Flex>
			<Spacer style={{ height: 100 }} />
		</ThemeOverride>
	);
};

export const StatisticsSection: React.FC = () => {
	return (
		<Flex column align="center" paddingVertical="xxl" paddingHorizontal="md">
			<Spacer style={{ height: 64 }} mobile={{ style: { height: 0 } }} />
			<Text
				size="xxxl"
				mobile={{ size: "xxl", style: { marginBottom: 32 } }}
				weight="semibold"
				align="center"
				style={{ marginBottom: 48 }}
			>
				By the Numbers
			</Text>
			<Flex
				gap="xl"
				wrap
				justify="center"
				align="stretch"
				style={{ maxWidth: 640, width: "100%" }}
			>
				<StatCard value="82%" description="Referral processing time reduced" />
				<StatCard value="95%" description="Authorization success rate" />
				<StatCard
					value="70%"
					description="Reduction in administrative burden"
				/>
			</Flex>
			<Spacer style={{ height: 64 }} />
			<Flex style={{ position: "relative", zIndex: 2 }}>
				<TestimonialCard
					quote="Lorem ipsum dolor sit amet, consectetur adipiscing elit. Sed do eiusmod tempor incididunt ut labore et dolore magna aliqua."
					avatarAlt="Kasey Hernandez"
					name="Kasey Hernandez"
					title="Medical Assistant, Marfa Country Clinic"
				/>
			</Flex>
		</Flex>
	);
};
